/*
 * Branded Best homepage: make "add your logo first" obvious (Nick, 2026-10-03: "make it evident that they drop their
 * logo in first thing to see it on every product… high converting… keep the aesthetic").
 *  1. The hero tumbler shows what happens: a few sample marks take turns on it in polished gold, between turns of our
 *     own photographed mark; the moment a visitor adds a logo, theirs is on the tumbler (and on every piece below).
 *     The photograph never changes or waits: a canvas over it lays a clean patch of the tumbler (from a copy of the
 *     photo with the mark removed, bb-house-hero-*-blank.jpg) and the logo, wrapped round the cylinder.
 *  2. Phones: once the logo box has scrolled away and no logo is in yet, a slim bar at the bottom offers it again.
 * Reads Nick's logo bar (bb-catalog-logo: its .logo and file input); never writes to it or to the order.
 */
(() => {
  const root = document.documentElement;
  const section = document.querySelector("#MainContent [id$='__bb_catalog_logo']");
  const bar = section && section.querySelector('bb-catalog-logo');
  if (!section || !bar) return;
  let blank = {};
  try { blank = JSON.parse(root.getAttribute('data-bbh-hero-blank') || '{}'); } catch (e) {}

  // Where the mark sits on the tumbler, as a share of each photo (measured on the photos, 2026-10-03), and the
  // tumbler's centre line and radius at that height (for the wrap).
  const SPOT = {
    wide: { ratio: 2688 / 1520, cx: 0.7359, cy: 0.3829, w: 0.155, h: 0.15, axis: 0.7318, r: 0.1023 },
    phone: { ratio: 1013 / 1520, cx: 0.5089, cy: 0.3829, w: 0.41, h: 0.15, axis: 0.5005, r: 0.2715 },
  };

  // ---------- the overlay canvas ----------
  const canvas = document.createElement('canvas');
  canvas.className = 'bbh-hero-logo';
  canvas.setAttribute('aria-hidden', 'true');
  section.prepend(canvas);
  const ctx = canvas.getContext('2d');
  const blankImg = {};
  const loadImg = (src) => new Promise((res) => { if (!src) return res(null); const i = new Image(); i.decoding = 'async'; i.onload = () => res(i); i.onerror = () => res(null); i.src = src; });

  // the photo's box on screen: desktop = the section's own background (as tall as the section, at its background-position:
  // bb-house-home.css, 2026-10-30, so the whole tumbler shows);
  // phones = the ::before band at the top (height 42svh, the photo at full height, centred)
  function frame() {
    const phone = matchMedia('(max-width: 989px)').matches;
    const sr = section.getBoundingClientRect();
    if (phone) {
      const h = parseFloat(getComputedStyle(section, '::before').height) || sr.width;
      const s = SPOT.phone, iw = h * s.ratio;
      return { phone, x: (sr.width - iw) / 2, y: 0, w: iw, h, box: { w: sr.width, h }, s, src: blank.ph };
    }
    const s = SPOT.wide, cs = getComputedStyle(section);
    const W = sr.width, H = sr.height, iw0 = s.ratio, k = H;
    const iw = iw0 * k, ih = k;
    const [px, py] = (cs.backgroundPosition || '72% 50%').split(' ').map((v) => parseFloat(v) / 100);
    const big = /hero-2688/.test(cs.backgroundImage);
    return { phone, x: (W - iw) * (isNaN(px) ? 0.72 : px), y: (H - ih) * (isNaN(py) ? 0.5 : py), w: iw, h: ih, box: { w: W, h: H }, s, src: big ? blank.lg : blank.md };
  }

  // polished gold on a mask, wrapped round the tumbler: columns follow the cylinder, light falls off at the sides.
  // false = nothing drawn (no logo shape, or the clean copy of this photo isn't in yet): our own mark stays
  function paint(mask) {
    shown = mask;
    const f = frame();
    const dpr = Math.min(2, devicePixelRatio || 1);
    canvas.style.width = f.box.w + 'px'; canvas.style.height = f.box.h + 'px';
    canvas.width = Math.round(f.box.w * dpr); canvas.height = Math.round(f.box.h * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, f.box.w, f.box.h);
    const img = blankImg[f.src];
    if (!mask || !mask.width || !mask.height || !img) return false;
    const s = f.s;
    const spot = { cx: f.x + s.cx * f.w, cy: f.y + s.cy * f.h, w: s.w * f.w, h: s.h * f.h };
    const axis = f.x + s.axis * f.w, R = s.r * f.w;
    // 1. a clean patch of the tumbler where our mark is (a soft-edged ellipse of the copy without it)
    const pw = spot.w * 1.25, ph = spot.h * 1.5;
    ctx.save();
    ctx.beginPath(); ctx.ellipse(spot.cx, spot.cy, pw / 2, ph / 2, 0, 0, Math.PI * 2); ctx.clip();
    ctx.drawImage(img, f.x, f.y, f.w, f.h);
    ctx.restore();
    // 2. the logo, fitted in the spot (keeps its shape), drawn flat first
    const ar = mask.width / mask.height;
    let lw = spot.w, lh = lw / ar;
    if (lh > spot.h) { lh = spot.h; lw = lh * ar; }
    const flat = document.createElement('canvas');
    flat.width = Math.max(1, Math.round(lw * dpr)); flat.height = Math.max(1, Math.round(lh * dpr));
    const fg = flat.getContext('2d');
    fg.imageSmoothingQuality = 'high';
    fg.drawImage(mask, 0, 0, flat.width, flat.height);
    fg.globalCompositeOperation = 'source-in';
    const gold = fg.createLinearGradient(0, 0, flat.width, flat.height * 0.35);
    [['0', '#a8802f'], ['0.18', '#e9c97b'], ['0.34', '#c79a46'], ['0.52', '#f4dc9b'], ['0.7', '#b98b3a'], ['0.86', '#e5c272'], ['1', '#a77d2d']]
      .forEach(([o, c]) => gold.addColorStop(+o, c));
    fg.fillStyle = gold; fg.fillRect(0, 0, flat.width, flat.height);
    // 3. wrapped: each screen column takes the flat column at the same arc length round the cylinder
    const y0 = spot.cy - lh / 2;
    const step = 1 / dpr;
    const theta0 = Math.asin(Math.max(-0.98, Math.min(0.98, (spot.cx - axis) / R)));   // the spot's centre on the body
    const xa = axis + R * Math.sin(theta0 - lw / 2 / R), xb = axis + R * Math.sin(theta0 + lw / 2 / R);
    for (let x = xa; x < xb; x += step) {
      const t = (x - axis) / R;                                   // where this screen column is on the cylinder
      if (Math.abs(t) >= 0.98) continue;
      const sx = ((Math.asin(t) - theta0) * R + lw / 2) * dpr;    // the flat column at the same arc length
      if (sx < 0 || sx >= flat.width) continue;
      ctx.globalAlpha = Math.max(0.35, Math.sqrt(1 - t * t));      // the turn of the body darkens the far side
      ctx.drawImage(flat, sx, 0, Math.max(1, step * dpr), flat.height, x, y0, step + 0.6, lh);
    }
    ctx.globalAlpha = 1;
    return true;
  }
  const tryPaint = (mask) => { try { return paint(mask); } catch (e) { return false; } };

  // a mask from an SVG (the sample marks) or from Nick's logo bar (a canvas with the logo's coverage in alpha).
  // Safari gives an SVG image no size unless it says so: the shape comes from the viewBox, never from the browser.
  const svgMask = (svg) => loadImg('data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg)).then((img) => {
    if (!img) return null;
    const vb = (svg.match(/viewBox="([^"]+)"/) || [])[1];
    const [, , vw, vh] = vb ? vb.split(/[\s,]+/).map(Number) : [0, 0, img.width, img.height];
    if (!(vw > 0 && vh > 0)) return null;
    const c = document.createElement('canvas'); c.width = 900; c.height = Math.round(900 * vh / vw);
    try { c.getContext('2d').drawImage(img, 0, 0, c.width, c.height); } catch (e) { return null; }
    return c;
  });
  const SAMPLES = [
    // a club crest
    `<svg xmlns="http://www.w3.org/2000/svg" width="900" height="459" viewBox="0 0 400 204"><path d="M200 14 L256 34 V86 C256 124 232 148 200 162 C168 148 144 124 144 86 V34 Z" fill="none" stroke="#000" stroke-width="8"/><text x="200" y="112" text-anchor="middle" font-family="Georgia,serif" font-size="72" font-weight="700">H</text><text x="200" y="194" text-anchor="middle" font-family="Georgia,serif" font-size="23" letter-spacing="5">HARBOR GOLF CLUB</text></svg>`,
    // a firm's wordmark
    `<svg xmlns="http://www.w3.org/2000/svg" width="900" height="315" viewBox="-20 0 400 140"><path d="M180 6 L188 38 L220 46 L188 54 L180 86 L172 54 L140 46 L172 38 Z" fill="#000"/><text x="180" y="128" text-anchor="middle" font-family="Helvetica,Arial,sans-serif" font-size="40" font-weight="700" letter-spacing="9">NORTHLINE</text></svg>`,
    // a round emblem
    `<svg xmlns="http://www.w3.org/2000/svg" width="900" height="900" viewBox="0 0 220 220"><circle cx="110" cy="110" r="100" fill="none" stroke="#000" stroke-width="7"/><circle cx="110" cy="110" r="86" fill="none" stroke="#000" stroke-width="2"/><path d="M58 128 L94 74 L112 98 L130 68 L164 128 Z" fill="#000"/><text x="110" y="158" text-anchor="middle" font-family="Helvetica,Arial,sans-serif" font-size="14" font-weight="700" letter-spacing="3">PEAK CAPITAL</text></svg>`,
  ];

  let samples = [], timer = 0, mode = 'idle', own = null, inView = true;
  const show = (on) => { canvas.classList.toggle('is-on', on); };
  let ready = false;
  // the homepage first shows our own sample mark on the pieces (bb-house-motion.js marks the bar data-bbh-sample):
  // that is not the visitor's logo
  const visitorMask = () => (!bar.hasAttribute('data-bbh-sample') && bar.logo && bar.logo.mask) || null;

  function cycle(i) {
    clearTimeout(timer);
    if (mode !== 'samples' || !samples.length) return;
    if (!inView || document.hidden) { timer = setTimeout(() => cycle(i), 800); return; }
    const k = i % (samples.length * 2);
    if (k % 2 === 0) { show(false); timer = setTimeout(() => cycle(i + 1), k === 0 ? 2600 : 1500); }    // our own mark
    else {                                                                                              // a sample
      const ok = tryPaint(samples[(k - 1) / 2]);
      show(ok); timer = setTimeout(() => cycle(i + 1), ok ? 2600 : 400);
    }
  }
  function update() {
    if (!ready) return;
    const m = visitorMask();
    if (m) {                                   // their logo: on the tumbler, and it stays
      mode = 'own'; clearTimeout(timer);
      if (own !== m) own = tryPaint(m) ? m : null;
      show(own === m);
    } else if (mode !== 'samples') {
      own = null; mode = 'samples'; show(false);
      timer = setTimeout(() => cycle(1), 1200);
    }
    nudge();
  }

  // the logo bar shows its chip once a logo is in, and hides it when it's removed: follow that
  const chip = bar.querySelector('[data-chip]');
  const watch = new MutationObserver(() => setTimeout(update, 60));
  if (chip) watch.observe(chip, { attributes: true, attributeFilter: ['hidden'] });
  watch.observe(bar, { attributes: true, attributeFilter: ['data-bbh-sample'] });
  new IntersectionObserver((es) => { inView = es[0].isIntersecting; }).observe(section);
  // the photo moves when the section changes size (the window, or the logo box growing once a logo is in): redraw
  let rz = 0, shown = null;
  const redraw = () => {
    cancelAnimationFrame(rz);
    rz = requestAnimationFrame(() => {
      if (mode === 'own') { own = null; update(); }
      else if (canvas.classList.contains('is-on') && shown) show(tryPaint(shown));
    });
  };
  addEventListener('resize', redraw);
  new ResizeObserver(redraw).observe(section);

  // ---------- phones: the logo again, at the bottom, once the box has scrolled away ----------
  let nudgeEl = null, boxGone = false;
  function nudge() {
    if (!nudgeEl) return;
    nudgeEl.hidden = !(boxGone && !visitorMask() && matchMedia('(max-width: 749px)').matches);
  }
  const file = bar.querySelector('[data-file]');
  if (file) {
    nudgeEl = document.createElement('div');
    nudgeEl.className = 'bbh-logo-nudge';
    nudgeEl.hidden = true;
    nudgeEl.innerHTML = '<span class="bbh-logo-nudge__text">See your logo on every piece.</span><button type="button" class="bbh-logo-nudge__btn">Add your logo</button>';
    nudgeEl.querySelector('button').addEventListener('click', () => file.click());
    document.body.appendChild(nudgeEl);
    const panel = bar.closest('.bb-cl') || bar;
    // gone = the logo box has scrolled up past the top of the screen (one measurement per scroll event: cheap)
    const check = () => { boxGone = panel.getBoundingClientRect().bottom < 0; nudge(); };
    addEventListener('scroll', check, { passive: true });
    addEventListener('resize', check);
    check();
  }

  // ---------- start: after the photo is up (never ahead of it) ----------
  const keep = (src) => loadImg(src).then((i) => { if (i) blankImg[src] = i; });
  const go = () => {
    let first = null;
    try { first = frame().src; } catch (e) {}
    Promise.all([keep(first), ...SAMPLES.map(svgMask)]).then(([, ...ms]) => {
      samples = ms.filter(Boolean);
      ready = true;
      update();
      Promise.all([blank.lg, blank.md, blank.ph].filter((u) => u && u !== first).map(keep)).then(update);
    });
  };
  if (document.readyState === 'complete') setTimeout(go, 600); else addEventListener('load', () => setTimeout(go, 600), { once: true });
})();
